/**
 * Plan Tier Management Component (Phase 2)
 * Super-admin interface for viewing and setting a school's subscription
 * plan tier and platform pricing (unit_price / monthly_unit_price / billing_plan).
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Row, Col, Card, Button, Form, Alert, Spinner, Badge
} from 'react-bootstrap';
import { getBillingConfig, updateBillingConfig } from '../../services/billingPhase2Service';
import schoolsService from '../../services/schoolsService';
import { CreditCard, CheckCircle, Rocket, Star, TrendUp, Crown } from '@phosphor-icons/react';
import './BulkDiscountManagement.css';

// 'starter' keeps its stored value from before a 4th tier existed — only
// its label changed to "Basic" when Standard was inserted below Growth.
// See campusio_backend/services/plan_gating.py's PlanTier docstring.
const PLAN_TIERS = [
  {
    value: 'starter',
    label: 'Basic',
    icon: Rocket,
    description: 'Entry tier',
  },
  {
    value: 'standard',
    label: 'Standard',
    icon: Star,
    description: 'Adds Academic Reports, Staff Attendance, Announcements & Surveys, Online Payments',
  },
  {
    value: 'growth',
    label: 'Growth',
    icon: TrendUp,
    description: 'Adds Student Safety & Pickup, Gate Attendance, Transport, Homework, Integrations & Advanced Finance',
  },
  {
    value: 'enterprise',
    label: 'Enterprise',
    icon: Crown,
    description: 'Everything in Growth, plus Payroll, ID Cards, and QR Check-In & Custody Controls',
  },
];

export const PlanTierManagement = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Every config field is per-school — a super admin has no school of their
  // own, so one must be picked first, same pattern as BulkDiscountManagement.
  const [schools, setSchools] = useState([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState('');
  const [config, setConfig] = useState(null);

  // Editable form state
  const [planTier, setPlanTier] = useState('starter');
  const [unitPrice, setUnitPrice] = useState('');
  const [monthlyUnitPrice, setMonthlyUnitPrice] = useState('');
  const [billingPlan, setBillingPlan] = useState('termly');
  const [gracePeriodDays, setGracePeriodDays] = useState('');
  const [defaultSuspensionDays, setDefaultSuspensionDays] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const data = await schoolsService.listSchools();
        const list = Array.isArray(data) ? data : (data.schools || data.data || []);
        setSchools(list);
        if (list.length > 0) {
          setSelectedSchoolId(list[0].id);
        }
      } catch (err) {
        setError('Failed to load schools list');
      }
    })();
  }, []);

  const loadConfig = useCallback(async () => {
    if (!selectedSchoolId) return;
    setLoading(true);
    setError(null);

    try {
      const data = await getBillingConfig(selectedSchoolId);
      setConfig(data);
      setPlanTier(data.plan_tier || 'starter');
      setUnitPrice(data.unit_price ?? '');
      setMonthlyUnitPrice(data.monthly_unit_price ?? '');
      setBillingPlan(data.billing_plan || 'termly');
      setGracePeriodDays(data.grace_period_days ?? '');
      setDefaultSuspensionDays(data.default_suspension_days ?? '');
    } catch (err) {
      setError(err.error || 'Failed to load billing configuration');
    } finally {
      setLoading(false);
    }
  }, [selectedSchoolId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleSave = async () => {
    setError(null);
    setSuccess(null);

    if (!selectedSchoolId) {
      setError('Select a school first');
      return;
    }

    setSaving(true);

    try {
      const updated = await updateBillingConfig(selectedSchoolId, {
        plan_tier: planTier,
        unit_price: unitPrice === '' ? undefined : parseFloat(unitPrice),
        monthly_unit_price: monthlyUnitPrice === '' ? undefined : parseFloat(monthlyUnitPrice),
        billing_plan: billingPlan,
        grace_period_days: gracePeriodDays === '' ? undefined : parseInt(gracePeriodDays, 10),
        default_suspension_days: defaultSuspensionDays === '' ? undefined : parseInt(defaultSuspensionDays, 10),
      });
      setConfig(updated);
      setSuccess('Billing configuration updated successfully.');
    } catch (err) {
      setError(err.error || 'Failed to update billing configuration');
    } finally {
      setSaving(false);
    }
  };

  const selectedSchoolName = schools.find(s => s.id === selectedSchoolId)?.name || '';

  const cardOnFileLabel = () => {
    if (!config) return null;
    if (config.paystack_card_last4) {
      const brand = config.paystack_card_brand
        ? config.paystack_card_brand.charAt(0).toUpperCase() + config.paystack_card_brand.slice(1)
        : 'Card';
      return `${brand} •••• ${config.paystack_card_last4}`;
    }
    return 'No card on file — school pays via manual checkout link';
  };

  return (
    <Container fluid className="bulk-discount-management mt-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <h2 className="section-title"><CreditCard size={20} className="me-2" /> Plan & Pricing</h2>
          <p className="text-muted">View and set a school's subscription plan tier and platform pricing</p>
        </Col>
        <Col md="auto" className="d-flex align-items-center gap-2">
          <Form.Select
            value={selectedSchoolId}
            onChange={(e) => setSelectedSchoolId(e.target.value)}
            style={{ minWidth: '220px' }}
          >
            {schools.length === 0 && <option value="">No schools found</option>}
            {schools.map((school) => (
              <option key={school.id} value={school.id}>{school.name}</option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          <Alert.Heading>Error</Alert.Heading>
          <p className="mb-0">{error}</p>
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          <Alert.Heading>Success</Alert.Heading>
          <p className="mb-0">{success}</p>
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : config ? (
        <>
          <Row>
            <Col lg={7} className="mb-3">
              <Card>
                <Card.Header className="bg-light">
                  <h5 className="mb-0">Plan Tier {selectedSchoolName && `— ${selectedSchoolName}`}</h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    {PLAN_TIERS.map((tier) => {
                      const Icon = tier.icon;
                      const isSelected = planTier === tier.value;
                      return (
                        <Col md={4} key={tier.value} className="mb-3">
                          <Card
                            className={`h-100 ${isSelected ? 'border-primary' : ''}`}
                            style={{ cursor: 'pointer', borderWidth: isSelected ? '2px' : '1px' }}
                            onClick={() => setPlanTier(tier.value)}
                          >
                            <Card.Body>
                              <Form.Check
                                type="radio"
                                name="planTier"
                                id={`plan-tier-${tier.value}`}
                                checked={isSelected}
                                onChange={() => setPlanTier(tier.value)}
                                label={
                                  <span className="fw-bold d-flex align-items-center gap-2">
                                    <Icon size={18} weight="fill" />
                                    {tier.label}
                                  </span>
                                }
                              />
                              <small className="text-muted d-block mt-2">{tier.description}</small>
                            </Card.Body>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>

                  <hr />

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Unit Price (per student, termly)</Form.Label>
                        <div className="input-group">
                          <span className="input-group-text">GHS</span>
                          <Form.Control
                            type="number"
                            value={unitPrice}
                            onChange={(e) => setUnitPrice(e.target.value)}
                            min="0"
                            step="0.01"
                          />
                        </div>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Monthly Unit Price (per student)</Form.Label>
                        <div className="input-group">
                          <span className="input-group-text">GHS</span>
                          <Form.Control
                            type="number"
                            value={monthlyUnitPrice}
                            onChange={(e) => setMonthlyUnitPrice(e.target.value)}
                            min="0"
                            step="0.01"
                          />
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Billing Plan</Form.Label>
                    <Form.Select
                      value={billingPlan}
                      onChange={(e) => setBillingPlan(e.target.value)}
                    >
                      <option value="termly">Termly</option>
                      <option value="monthly">Monthly</option>
                    </Form.Select>
                  </Form.Group>

                  <hr />

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Late Fee Grace Period</Form.Label>
                        <div className="input-group">
                          <Form.Control
                            type="number"
                            value={gracePeriodDays}
                            onChange={(e) => setGracePeriodDays(e.target.value)}
                            min="0"
                            step="1"
                          />
                          <span className="input-group-text">days overdue</span>
                        </div>
                        <Form.Text muted>How long past the due date before a late fee is applied to this school's own invoice.</Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Suspension Grace Period</Form.Label>
                        <div className="input-group">
                          <Form.Control
                            type="number"
                            value={defaultSuspensionDays}
                            onChange={(e) => setDefaultSuspensionDays(e.target.value)}
                            min="0"
                            step="1"
                          />
                          <span className="input-group-text">days overdue</span>
                        </div>
                        <Form.Text muted>How long past the due date before this school's module access is suspended for non-payment.</Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={saving || !selectedSchoolId}
                  >
                    {saving ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={18} className="me-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={5} className="mb-3">
              <Card>
                <Card.Header className="bg-light">
                  <h5 className="mb-0">Auto-Renewal & Card on File</h5>
                </Card.Header>
                <Card.Body>
                  <p className="text-muted mb-2">
                    Read-only here — the school (or its own school admin) manages these from their billing dashboard.
                  </p>
                  <div className="mb-3">
                    <span className="text-muted d-block mb-1">Auto-Renew</span>
                    <Badge bg={config.auto_renew_enabled ? 'success' : 'secondary'} pill>
                      {config.auto_renew_enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                  <div>
                    <span className="text-muted d-block mb-1">Card on File</span>
                    <div className="d-flex align-items-center gap-2">
                      <CreditCard size={18} />
                      <span>{cardOnFileLabel()}</span>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      ) : (
        <div className="alert alert-info" role="alert">
          Select a school to view its plan and pricing.
        </div>
      )}
    </Container>
  );
};

export default PlanTierManagement;
